import { NotificationSeverity } from '@/components/notifications/Notification';
import { useResolveRouteOperatorMutation } from '../services';
import { useNavigate } from 'react-router-dom';
import { ShippingListApi } from '../models';
import { useNotification } from '@/hooks';
import { getErrorMessage } from '@/utils';
import { useCallback } from 'react';

const buildOperatorNameParam = (name: string | null): string =>
  name ? `&routeOperatorName=${encodeURIComponent(name)}` : '';

interface UseShippingRoutingProps {
  sortedData: ShippingListApi[];
  selectedShippings: Set<string>;
  logisticOperatorId?: string;
  maxSelectable: number;
}

export const useShippingRouting = ({
  sortedData,
  selectedShippings,
  maxSelectable,
}: UseShippingRoutingProps) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [resolveRouteOperator] = useResolveRouteOperatorMutation();

  const resolveRouteOperatorId = useCallback(
    async (
      shippingData: ShippingListApi[],
      shippingNumbers: string[]
    ): Promise<{ id: string; name: string | null } | null> => {
      const uniqueOperatorIds = new Set(
        shippingData
          .map((s) => s.logisticOperatorId)
          .filter((id): id is string => Boolean(id?.trim()))
      );

      if (uniqueOperatorIds.size === 0) {
        showNotification(
          'Los envíos seleccionados no tienen un operador logístico asignado. Por favor, seleccione un operador logístico en los filtros de búsqueda.',
          NotificationSeverity.error
        );
        return null;
      }

      try {
        const result = await resolveRouteOperator({
          shippingNumbers: shippingNumbers
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n) && n > 0),
        }).unwrap();

        if (result.mixed) {
          showNotification(
            `La ruta va a quedar a nombre de ${
              result.logisticOperatorName ?? 'su operador casa central'
            }`,
            NotificationSeverity.info
          );
        }

        return {
          id: result.logisticOperatorId,
          name: result.logisticOperatorName,
        };
      } catch (error: unknown) {
        if (uniqueOperatorIds.size === 1) {
          return { id: Array.from(uniqueOperatorIds)[0], name: null };
        }

        showNotification(
          getErrorMessage(
            error,
            'No se pudo determinar el operador logístico de la ruta'
          ),
          NotificationSeverity.error
        );
        return null;
      }
    },
    [resolveRouteOperator, showNotification]
  );

  const handleRouteSelected = useCallback(async () => {
    if (selectedShippings.size === 0) {
      showNotification(
        'Debe seleccionar al menos un envío para rutear',
        NotificationSeverity.warning
      );
      return;
    }

    if (selectedShippings.size > maxSelectable) {
      showNotification(
        `No se pueden rutear más de ${maxSelectable} envíos a la vez`,
        NotificationSeverity.error
      );
      return;
    }

    const selectedShippingNumbers = Array.from(selectedShippings);
    const selectedShippingData = sortedData.filter((s) =>
      selectedShippingNumbers.includes(String(s.number))
    );

    const routeOperator = await resolveRouteOperatorId(
      selectedShippingData,
      selectedShippingNumbers
    );
    if (!routeOperator) return;

    const url = `/business/shippings/routing?numbers=${selectedShippingNumbers.join(',')}&logisticOperatorId=${routeOperator.id}${buildOperatorNameParam(routeOperator.name)}`;
    navigate(url);
  }, [
    selectedShippings,
    sortedData,
    maxSelectable,
    resolveRouteOperatorId,
    showNotification,
    navigate,
  ]);

  const handleRouteAll = useCallback(async () => {
    const visibleNumbers = sortedData.map((s) => String(s.number));

    if (visibleNumbers.length === 0) {
      showNotification(
        'No hay envíos visibles para rutear',
        NotificationSeverity.warning
      );
      return;
    }

    if (visibleNumbers.length > maxSelectable) {
      showNotification(
        `No se pueden rutear más de ${maxSelectable} envíos a la vez. Hay ${visibleNumbers.length} envíos visibles.`,
        NotificationSeverity.error
      );
      return;
    }

    const routeOperator = await resolveRouteOperatorId(
      sortedData,
      visibleNumbers
    );
    if (!routeOperator) return;

    const url = `/business/shippings/routing?numbers=${visibleNumbers.join(',')}&logisticOperatorId=${routeOperator.id}${buildOperatorNameParam(routeOperator.name)}`;
    navigate(url);
  }, [
    sortedData,
    maxSelectable,
    resolveRouteOperatorId,
    showNotification,
    navigate,
  ]);

  return {
    handleRouteSelected,
    handleRouteAll,
  };
};
